<script setup>
import { ref, reactive, onMounted, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  product: {
    type: Object,
    default: () => ({}),
  },
  assistantId: {
    type: [Number, String],
    default: null,
  },
});
const store = useStore();
const { t } = useI18n();

const isEditing = ref(false);
const isSubmitting = ref(false);
const isLoading = ref(false);
const editingPriceId = ref(null);

const assistantRecord = computed(() =>
  store.getters?.['ailabAssistants/getRecord']
    ? store.getters['ailabAssistants/getRecord'](Number(props.assistantId))
    : null
);

const isAIGamer = computed(
  () => assistantRecord.value?.config?.wlab_sidebar_key === 'ai_gamer'
);

const promoStatusLabel = status => {
  const key = (status || '').toString().toLowerCase();
  if (key === 'active')
    return t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.STATUS.ACTIVE');
  if (key === 'inactive')
    return t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.STATUS.INACTIVE');
  return status;
};

const showPromoModal = ref(false);
const promoEditingItemId = ref(null);
const promoForm = ref({});

const defaultPromoMeta = () => ({
  basics: {
    name: '',
    code: '',
    type: 'FD', // FD/CB/VIP/REF/OTHER
    status: 'active',
    start_at: '',
    end_at: '',
    max_claims: null,
    per_user_limit: null,
  },
  deposit_withdraw: {
    min_deposit: null,
    max_deposit: null,
    min_withdraw: null,
    max_withdraw: null,
    withdraw_times_limit: null,
    channels: '',
    withdraw_time_note: '',
  },
  bonus: {
    percent: null,
    min_bonus: null,
    max_bonus: null,
    formula: '',
    wallet_type: 'bonus', // bonus/cash
    cancel_rule: '',
  },
  turnover: {
    turnover_x: null,
    base: 'deposit', // deposit/bonus/deposit_plus_bonus/bet
    eligible_rules: '',
    excluded_rules: '',
    contribution: '',
    deadline_days: null,
  },
  eligible: {
    providers: [], // ["PG","PP","JILI","EVO","SBO"]
    excluded_games: '',
    bet_limit: '',
    max_win: '',
  },
  eligibility: {
    customer_type: 'all', // new/existing/vip/all
    countries: [],
    currencies: [],
    one_per_person: true,
    one_per_ip: false,
    one_per_device: false,
    kyc_required: false,
  },
  claiming: {
    method: 'auto', // auto/code/admin
    preconditions: '',
  },
  cannot_claim: {
    reasons: '',
  },
  disqualifications: {
    rules: '',
  },
  compliance: {
    age_18_plus: true,
    no_guaranteed_wins: true,
    terms_may_change: true,
    notes: '',
  },
});

const deepClone = obj => JSON.parse(JSON.stringify(obj || {}));

// Get prices from store
const storePrices = computed(() => store.getters['aiCloser/getPrices']);

// Local reactive copy for editing
const priceData = reactive({
  items: [],
});

// Fetch prices on mount
onMounted(async () => {
  if (!props.assistantId) return;
  isLoading.value = true;
  try {
    // Use a high but capped limit for UI editing; server supports pagination for scale.
    await store.dispatch('aiCloser/fetchPrices', {
      assistantId: props.assistantId,
      params: { limit: 200, offset: 0 },
    });
  } catch (error) {
    // Prices may not exist yet
  } finally {
    isLoading.value = false;
  }
});

// Sync store prices to local data
watch(
  () => storePrices.value,
  newVal => {
    priceData.items = (newVal || []).map(p => ({
      id: p.id,
      country: p.country || '',
      currency: p.currency || '',
      price: p.price?.toString() || '',
      discountPrice: p.discount_price?.toString() || '',
      unit: p.unit || '',
      promoMeta: p.promo_meta ? deepClone(p.promo_meta) : {},
    }));
  },
  { immediate: true, deep: true }
);

const handleEdit = () => {
  isEditing.value = true;
};

const handleCancel = () => {
  isEditing.value = false;
  // Reset to store data
  priceData.items = (storePrices.value || []).map(p => ({
    id: p.id,
    country: p.country || '',
    currency: p.currency || '',
    price: p.price?.toString() || '',
    discountPrice: p.discount_price?.toString() || '',
    unit: p.unit || '',
    promoMeta: p.promo_meta ? deepClone(p.promo_meta) : {},
  }));
};

const openPromoDetails = item => {
  promoEditingItemId.value = item?.id || null;
  promoForm.value = deepClone(item?.promoMeta) || {};
  if (!Object.keys(promoForm.value || {}).length) {
    promoForm.value = defaultPromoMeta();
  }
  showPromoModal.value = true;
};

const closePromoDetails = () => {
  showPromoModal.value = false;
  promoEditingItemId.value = null;
  promoForm.value = {};
};

const savePromoDetailsToRow = () => {
  const id = promoEditingItemId.value;
  if (!id) return;
  const idx = (priceData.items || []).findIndex(i => i.id === id);
  if (idx === -1) return;
  priceData.items[idx].promoMeta = deepClone(promoForm.value || {});
};

const persistPromoDetails = async () => {
  if (!props.assistantId) return;
  const id = promoEditingItemId.value;
  if (!id) return;
  const item = (priceData.items || []).find(i => i.id === id);
  if (!item) return;

  isSubmitting.value = true;
  try {
    await store.dispatch('aiCloser/updatePrice', {
      assistantId: props.assistantId,
      priceId: id,
      data: {
        country: item.country,
        currency: item.currency,
        price: parseFloat(item.price) || 0,
        discount_price: parseFloat(item.discountPrice) || null,
        unit: item.unit,
        promo_meta: deepClone(promoForm.value || {}),
      },
    });
    // keep local in sync
    savePromoDetailsToRow();
    useAlert(
      t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.ALERTS.PROMO_DETAILS_SAVED')
    );
    closePromoDetails();
  } catch (error) {
    useAlert(
      error?.message ||
        t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.ALERTS.PROMO_DETAILS_SAVE_FAILED')
    );
  } finally {
    isSubmitting.value = false;
  }
};

const handleSave = async () => {
  if (!props.assistantId) return;
  isSubmitting.value = true;
  try {
    // Update all prices
    for (const item of priceData.items) {
      if (item.id) {
        await store.dispatch('aiCloser/updatePrice', {
          assistantId: props.assistantId,
          priceId: item.id,
          data: {
            country: item.country,
            currency: item.currency,
            price: parseFloat(item.price) || 0,
            discount_price: parseFloat(item.discountPrice) || null,
            unit: item.unit,
            promo_meta: item.promoMeta || {},
          },
        });
      }
    }
    useAlert(t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.ALERTS.SAVED'));
    isEditing.value = false;
  } catch (error) {
    useAlert(
      error?.message ||
        t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.ALERTS.SAVE_FAILED')
    );
  } finally {
    isSubmitting.value = false;
  }
};

const handleAddRow = async () => {
  if (!props.assistantId) return;
  try {
    await store.dispatch('aiCloser/createPrice', {
      assistantId: props.assistantId,
      data: {
        country: isAIGamer.value ? 'TH' : 'New Country',
        currency: isAIGamer.value ? 'THB' : 'USD',
        price: 0,
        unit: isAIGamer.value
          ? 'TYPE:FD PCT:0 TO:0 D:0 G:ALL EL:ALL CLAIM:AUTO'
          : 'box',
        promo_meta: isAIGamer.value ? defaultPromoMeta() : {},
      },
    });
    useAlert(t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.ALERTS.ROW_ADDED'));
  } catch (error) {
    useAlert(
      error?.message ||
        t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.ALERTS.ADD_FAILED')
    );
  }
};

const handleDeleteRow = async priceId => {
  if (!props.assistantId || !priceId) return;
  try {
    await store.dispatch('aiCloser/deletePrice', {
      assistantId: props.assistantId,
      priceId,
    });
    useAlert(t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.ALERTS.ROW_DELETED'));
  } catch (error) {
    useAlert(
      error?.message ||
        t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.ALERTS.DELETE_FAILED')
    );
  }
};

const handleLoadDefault = () => {
  useAlert(
    isAIGamer.value
      ? t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.ALERTS.ENCODING_GUIDE_LOADED')
      : t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.ALERTS.DEFAULT_TEMPLATE_LOADED')
  );
};
</script>

<template>
  <div class="price-tab">
    <!-- Toolbar -->
    <div class="toolbar">
      <button class="default-btn" @click="handleLoadDefault">
        <span class="i-lucide-database size-4" />
        {{
          isAIGamer
            ? t(
                'AILAB.ASSISTANT_V2.PRODUCT_PRICE.TOOLBAR.DEFAULT_BUTTON.AIGAMER'
              )
            : t(
                'AILAB.ASSISTANT_V2.PRODUCT_PRICE.TOOLBAR.DEFAULT_BUTTON.DEFAULT'
              )
        }}
      </button>
      <div class="toolbar-actions">
        <template v-if="isEditing">
          <button class="cancel-btn" @click="handleCancel">
            <span class="i-lucide-x size-4" />
            {{ t('AILAB.FORM.CANCEL') }}
          </button>
          <button class="save-btn" :disabled="isSubmitting" @click="handleSave">
            <span
              v-if="isSubmitting"
              class="i-lucide-loader-2 size-4 animate-spin"
            />
            <span v-else class="i-lucide-save size-4" />
            {{ t('AILAB.FORM.SAVE') }}
          </button>
        </template>
        <button v-else class="edit-btn" @click="handleEdit">
          <span class="i-lucide-pencil size-4" />
          {{ t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.TOOLBAR.EDIT') }}
        </button>
      </div>
    </div>

    <!-- AI gamer guidance -->
    <div v-if="isAIGamer" class="promo-guide">
      <div class="promo-guide-title">
        {{ t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.GUIDE.TITLE') }}
      </div>
      <ul class="promo-guide-list">
        <li>
          {{
            t(
              'AILAB.ASSISTANT_V2.PRODUCT_PRICE.GUIDE.ITEMS.USE_DETAILS_PREFIX'
            )
          }}
          <b>{{ t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.ACTIONS.DETAILS') }}</b>
          {{
            t(
              'AILAB.ASSISTANT_V2.PRODUCT_PRICE.GUIDE.ITEMS.USE_DETAILS_SUFFIX'
            )
          }}
        </li>
        <li>
          {{
            t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.GUIDE.ITEMS.POLICY_PREFIX')
          }}
          <b>{{
            t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.GUIDE.ITEMS.FAQ_TEMPLATES')
          }}</b>
          {{
            t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.GUIDE.ITEMS.POLICY_SUFFIX')
          }}
        </li>
      </ul>
      <div class="promo-guide-example">
        {{ t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.GUIDE.TIP_PREFIX') }}
        <code>[FD] First Deposit 100% (TH)</code>
      </div>
    </div>

    <!-- Price Table -->
    <div class="price-container">
      <table class="price-table">
        <thead>
          <tr>
            <th v-if="isAIGamer">
              {{
                t(
                  'AILAB.ASSISTANT_V2.PRODUCT_PRICE.TABLE.HEADERS.PROMOTION_NAME'
                )
              }}
            </th>
            <th>
              {{
                t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.TABLE.HEADERS.COUNTRY')
              }}
            </th>
            <th>
              {{
                t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.TABLE.HEADERS.CURRENCY')
              }}
            </th>
            <th v-if="!isAIGamer">
              {{ t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.TABLE.HEADERS.PRICE') }}
            </th>
            <th v-if="!isAIGamer">
              {{
                t(
                  'AILAB.ASSISTANT_V2.PRODUCT_PRICE.TABLE.HEADERS.DISCOUNT_PRICE'
                )
              }}
            </th>
            <th v-if="!isAIGamer">
              {{ t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.TABLE.HEADERS.UNIT') }}
            </th>
            <th v-if="isAIGamer || isEditing">
              {{
                t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.TABLE.HEADERS.ACTIONS')
              }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in priceData.items" :key="item.id">
            <td v-if="isAIGamer">
              <div class="promo-name-cell">
                <div class="promo-name">
                  {{
                    item?.promoMeta?.basics?.name ||
                    t(
                      'AILAB.ASSISTANT_V2.PRODUCT_PRICE.TABLE.UNNAMED_PROMOTION'
                    )
                  }}
                </div>
                <div class="promo-sub">
                  <span v-if="item?.promoMeta?.basics?.type">
                    {{
                      t(
                        'AILAB.ASSISTANT_V2.PRODUCT_PRICE.TABLE.TYPE_PREFIX',
                        { type: item.promoMeta.basics.type }
                      )
                    }}
                  </span>
                  <span v-if="item?.promoMeta?.basics?.status">
                    {{ item?.promoMeta?.basics?.type ? ' • ' : ''
                    }}{{ promoStatusLabel(item.promoMeta.basics.status) }}
                  </span>
                </div>
              </div>
            </td>
            <td>
              <template v-if="isEditing">
                <input
                  v-model="item.country"
                  type="text"
                  class="table-input small"
                  :placeholder="
                    isAIGamer
                      ? t(
                          'AILAB.ASSISTANT_V2.PRODUCT_PRICE.PLACEHOLDERS.COUNTRY_AIGAMER'
                        )
                      : t(
                          'AILAB.ASSISTANT_V2.PRODUCT_PRICE.PLACEHOLDERS.COUNTRY'
                        )
                  "
                />
              </template>
              <template v-else>
                <div class="country-cell">
                  <span class="i-lucide-globe size-4" />
                  {{ item.country }}
                </div>
              </template>
            </td>
            <td>
              <template v-if="isEditing">
                <input
                  v-model="item.currency"
                  type="text"
                  class="table-input small"
                  :placeholder="
                    isAIGamer
                      ? t(
                          'AILAB.ASSISTANT_V2.PRODUCT_PRICE.PLACEHOLDERS.CURRENCY_AIGAMER'
                        )
                      : t(
                          'AILAB.ASSISTANT_V2.PRODUCT_PRICE.PLACEHOLDERS.CURRENCY'
                        )
                  "
                />
              </template>
              <template v-else>
                <span class="currency-badge">{{ item.currency }}</span>
              </template>
            </td>
            <td v-if="!isAIGamer">
              <template v-if="isEditing">
                <input
                  v-model="item.price"
                  type="text"
                  class="table-input"
                  :placeholder="
                    t(
                      'AILAB.ASSISTANT_V2.PRODUCT_PRICE.TABLE.PLACEHOLDERS.PRICE'
                    )
                  "
                />
              </template>
              <template v-else> {{ item.currency }} {{ item.price }} </template>
            </td>
            <td v-if="!isAIGamer">
              <template v-if="isEditing">
                <input
                  v-model="item.discountPrice"
                  type="text"
                  class="table-input"
                  :placeholder="
                    t(
                      'AILAB.ASSISTANT_V2.PRODUCT_PRICE.TABLE.PLACEHOLDERS.DISCOUNT_PRICE'
                    )
                  "
                />
              </template>
              <template v-else>
                <span class="discount-price"
                  >{{ item.currency }} {{ item.discountPrice }}</span
                >
              </template>
            </td>
            <td v-if="!isAIGamer">
              <template v-if="isEditing">
                <input
                  v-model="item.unit"
                  type="text"
                  class="table-input small"
                  :placeholder="
                    t(
                      'AILAB.ASSISTANT_V2.PRODUCT_PRICE.TABLE.PLACEHOLDERS.UNIT'
                    )
                  "
                />
              </template>
              <template v-else>
                {{
                  t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.TABLE.PER_UNIT', {
                    unit: item.unit,
                  })
                }}
              </template>
            </td>
            <td v-if="isAIGamer || isEditing">
              <div class="row-actions">
                <button
                  v-if="isAIGamer"
                  class="details-btn"
                  type="button"
                  @click="openPromoDetails(item)"
                >
                  {{ t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.ACTIONS.DETAILS') }}
                </button>
                <button
                  class="delete-btn"
                  type="button"
                  @click="handleDeleteRow(item.id)"
                >
                  <span class="i-lucide-trash-2 size-4" />
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Add New Price Row -->
      <div v-if="isEditing" class="add-row-container">
        <button class="add-row-btn" @click="handleAddRow">
          <span class="i-lucide-plus size-4" />
          {{
            isAIGamer
              ? t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.ACTIONS.ADD_PROMO_ROW')
              : t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.ACTIONS.ADD_PRICE_ROW')
          }}
        </button>
      </div>

      <!-- Empty State -->
      <div v-if="priceData.items.length === 0" class="empty-state">
        <span class="i-lucide-tag size-12 text-white/20" />
        <p>{{ t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.EMPTY.TITLE') }}</p>
        <button class="add-btn">
          <span class="i-lucide-plus size-4" />
          {{ t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.ACTIONS.ADD_PRICING') }}
        </button>
      </div>
    </div>

    <!-- Promotion Details Modal (AI gamer only) -->
    <Teleport to="body">
      <div
        v-if="showPromoModal"
        class="modal-overlay"
        @click.self="closePromoDetails"
      >
        <div class="modal-content">
          <div class="modal-header">
            <h3>{{ t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.MODAL.TITLE') }}</h3>
            <button
              class="modal-close"
              type="button"
              @click="closePromoDetails"
            >
              <span class="i-lucide-x size-5" />
            </button>
          </div>

          <div class="modal-body">
            <div class="modal-note">
              {{ t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.MODAL.NOTE') }}
            </div>

            <div class="section">
              <div class="section-title">
                {{ t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.SECTIONS.BASICS') }}
              </div>
              <div class="grid2">
                <div class="field">
                  <label>{{
                    t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.FIELDS.BASICS.NAME')
                  }}</label>
                  <input
                    v-model="promoForm.basics.name"
                    class="input"
                    :placeholder="
                      t(
                        'AILAB.ASSISTANT_V2.PRODUCT_PRICE.PLACEHOLDERS.PROMO_NAME_EXAMPLE'
                      )
                    "
                  />
                </div>
                <div class="field">
                  <label>{{
                    t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.FIELDS.BASICS.CODE')
                  }}</label>
                  <input
                    v-model="promoForm.basics.code"
                    class="input"
                    :placeholder="
                      t(
                        'AILAB.ASSISTANT_V2.PRODUCT_PRICE.PLACEHOLDERS.PROMO_CODE_EXAMPLE'
                      )
                    "
                  />
                </div>
                <div class="field">
                  <label>{{
                    t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.FIELDS.BASICS.TYPE')
                  }}</label>
                  <select v-model="promoForm.basics.type" class="input">
                    <option value="FD">
                      {{
                        t(
                          'AILAB.ASSISTANT_V2.PRODUCT_PRICE.OPTIONS.PROMO_TYPE.FD'
                        )
                      }}
                    </option>
                    <option value="CB">
                      {{
                        t(
                          'AILAB.ASSISTANT_V2.PRODUCT_PRICE.OPTIONS.PROMO_TYPE.CB'
                        )
                      }}
                    </option>
                    <option value="VIP">
                      {{
                        t(
                          'AILAB.ASSISTANT_V2.PRODUCT_PRICE.OPTIONS.PROMO_TYPE.VIP'
                        )
                      }}
                    </option>
                    <option value="REF">
                      {{
                        t(
                          'AILAB.ASSISTANT_V2.PRODUCT_PRICE.OPTIONS.PROMO_TYPE.REF'
                        )
                      }}
                    </option>
                    <option value="OTHER">
                      {{
                        t(
                          'AILAB.ASSISTANT_V2.PRODUCT_PRICE.OPTIONS.PROMO_TYPE.OTHER'
                        )
                      }}
                    </option>
                  </select>
                </div>
                <div class="field">
                  <label>{{
                    t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.FIELDS.BASICS.STATUS')
                  }}</label>
                  <select v-model="promoForm.basics.status" class="input">
                    <option value="active">
                      {{
                        t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.STATUS.ACTIVE')
                      }}
                    </option>
                    <option value="inactive">
                      {{
                        t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.STATUS.INACTIVE')
                      }}
                    </option>
                  </select>
                </div>
                <div class="field">
                  <label>{{
                    t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.FIELDS.BASICS.START')
                  }}</label>
                  <input
                    v-model="promoForm.basics.start_at"
                    class="input"
                    :placeholder="
                      t(
                        'AILAB.ASSISTANT_V2.PRODUCT_PRICE.PLACEHOLDERS.DATE_YYYY_MM_DD'
                      )
                    "
                  />
                </div>
                <div class="field">
                  <label>{{
                    t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.FIELDS.BASICS.END')
                  }}</label>
                  <input
                    v-model="promoForm.basics.end_at"
                    class="input"
                    :placeholder="
                      t(
                        'AILAB.ASSISTANT_V2.PRODUCT_PRICE.PLACEHOLDERS.DATE_YYYY_MM_DD'
                      )
                    "
                  />
                </div>
              </div>
            </div>

            <div class="section">
              <div class="section-title">
                {{
                  t(
                    'AILAB.ASSISTANT_V2.PRODUCT_PRICE.SECTIONS.DEPOSIT_WITHDRAW'
                  )
                }}
              </div>
              <div class="grid2">
                <div class="field">
                  <label>{{
                    t(
                      'AILAB.ASSISTANT_V2.PRODUCT_PRICE.FIELDS.DEPOSIT_WITHDRAW.MIN_DEPOSIT'
                    )
                  }}</label>
                  <input
                    v-model="promoForm.deposit_withdraw.min_deposit"
                    class="input"
                    :placeholder="
                      t(
                        'AILAB.ASSISTANT_V2.PRODUCT_PRICE.PLACEHOLDERS.NUMBER'
                      )
                    "
                  />
                </div>
                <div class="field">
                  <label>{{
                    t(
                      'AILAB.ASSISTANT_V2.PRODUCT_PRICE.FIELDS.DEPOSIT_WITHDRAW.MAX_DEPOSIT'
                    )
                  }}</label>
                  <input
                    v-model="promoForm.deposit_withdraw.max_deposit"
                    class="input"
                    :placeholder="
                      t(
                        'AILAB.ASSISTANT_V2.PRODUCT_PRICE.PLACEHOLDERS.NUMBER'
                      )
                    "
                  />
                </div>
                <div class="field">
                  <label>{{
                    t(
                      'AILAB.ASSISTANT_V2.PRODUCT_PRICE.FIELDS.DEPOSIT_WITHDRAW.MIN_WITHDRAW'
                    )
                  }}</label>
                  <input
                    v-model="promoForm.deposit_withdraw.min_withdraw"
                    class="input"
                    :placeholder="
                      t(
                        'AILAB.ASSISTANT_V2.PRODUCT_PRICE.PLACEHOLDERS.NUMBER'
                      )
                    "
                  />
                </div>
                <div class="field">
                  <label>{{
                    t(
                      'AILAB.ASSISTANT_V2.PRODUCT_PRICE.FIELDS.DEPOSIT_WITHDRAW.MAX_WITHDRAW'
                    )
                  }}</label>
                  <input
                    v-model="promoForm.deposit_withdraw.max_withdraw"
                    class="input"
                    :placeholder="
                      t(
                        'AILAB.ASSISTANT_V2.PRODUCT_PRICE.PLACEHOLDERS.NUMBER'
                      )
                    "
                  />
                </div>
                <div class="field">
                  <label>{{
                    t(
                      'AILAB.ASSISTANT_V2.PRODUCT_PRICE.FIELDS.DEPOSIT_WITHDRAW.CHANNELS'
                    )
                  }}</label>
                  <input
                    v-model="promoForm.deposit_withdraw.channels"
                    class="input"
                    :placeholder="
                      t(
                        'AILAB.ASSISTANT_V2.PRODUCT_PRICE.PLACEHOLDERS.CHANNELS_EXAMPLE'
                      )
                    "
                  />
                </div>
                <div class="field">
                  <label>{{
                    t(
                      'AILAB.ASSISTANT_V2.PRODUCT_PRICE.FIELDS.DEPOSIT_WITHDRAW.WITHDRAW_TIME'
                    )
                  }}</label>
                  <input
                    v-model="promoForm.deposit_withdraw.withdraw_time_note"
                    class="input"
                    :placeholder="
                      t(
                        'AILAB.ASSISTANT_V2.PRODUCT_PRICE.PLACEHOLDERS.WITHDRAW_TIME_EXAMPLE'
                      )
                    "
                  />
                </div>
              </div>
            </div>

            <div class="section">
              <div class="section-title">
                {{
                  t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.SECTIONS.BONUS_DETAILS')
                }}
              </div>
              <div class="grid2">
                <div class="field">
                  <label>{{
                    t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.FIELDS.BONUS.PERCENT')
                  }}</label>
                  <input
                    v-model="promoForm.bonus.percent"
                    class="input"
                    :placeholder="
                      t(
                        'AILAB.ASSISTANT_V2.PRODUCT_PRICE.PLACEHOLDERS.PERCENT_EXAMPLE'
                      )
                    "
                  />
                </div>
                <div class="field">
                  <label>{{
                    t(
                      'AILAB.ASSISTANT_V2.PRODUCT_PRICE.FIELDS.BONUS.MAX_BONUS'
                    )
                  }}</label>
                  <input
                    v-model="promoForm.bonus.max_bonus"
                    class="input"
                    :placeholder="
                      t(
                        'AILAB.ASSISTANT_V2.PRODUCT_PRICE.PLACEHOLDERS.NUMBER'
                      )
                    "
                  />
                </div>
                <div class="field">
                  <label>{{
                    t(
                      'AILAB.ASSISTANT_V2.PRODUCT_PRICE.FIELDS.BONUS.WALLET_TYPE'
                    )
                  }}</label>
                  <select v-model="promoForm.bonus.wallet_type" class="input">
                    <option value="bonus">
                      {{
                        t(
                          'AILAB.ASSISTANT_V2.PRODUCT_PRICE.OPTIONS.WALLET_TYPE.BONUS'
                        )
                      }}
                    </option>
                    <option value="cash">
                      {{
                        t(
                          'AILAB.ASSISTANT_V2.PRODUCT_PRICE.OPTIONS.WALLET_TYPE.CASH'
                        )
                      }}
                    </option>
                  </select>
                </div>
                <div class="field">
                  <label>{{
                    t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.FIELDS.BONUS.FORMULA')
                  }}</label>
                  <input
                    v-model="promoForm.bonus.formula"
                    class="input"
                    :placeholder="
                      t(
                        'AILAB.ASSISTANT_V2.PRODUCT_PRICE.PLACEHOLDERS.FORMULA_EXAMPLE'
                      )
                    "
                  />
                </div>
              </div>
            </div>

            <div class="section">
              <div class="section-title">
                {{ t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.SECTIONS.TURNOVER') }}
              </div>
              <div class="grid2">
                <div class="field">
                  <label>{{
                    t(
                      'AILAB.ASSISTANT_V2.PRODUCT_PRICE.FIELDS.TURNOVER.TURNOVER_X'
                    )
                  }}</label>
                  <input
                    v-model="promoForm.turnover.turnover_x"
                    class="input"
                    :placeholder="
                      t(
                        'AILAB.ASSISTANT_V2.PRODUCT_PRICE.PLACEHOLDERS.TURNOVER_X_EXAMPLE'
                      )
                    "
                  />
                </div>
                <div class="field">
                  <label>{{
                    t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.FIELDS.TURNOVER.BASE')
                  }}</label>
                  <select v-model="promoForm.turnover.base" class="input">
                    <option value="deposit">
                      {{
                        t(
                          'AILAB.ASSISTANT_V2.PRODUCT_PRICE.OPTIONS.TURNOVER_BASE.DEPOSIT'
                        )
                      }}
                    </option>
                    <option value="bonus">
                      {{
                        t(
                          'AILAB.ASSISTANT_V2.PRODUCT_PRICE.OPTIONS.TURNOVER_BASE.BONUS'
                        )
                      }}
                    </option>
                    <option value="deposit_plus_bonus">
                      {{
                        t(
                          'AILAB.ASSISTANT_V2.PRODUCT_PRICE.OPTIONS.TURNOVER_BASE.DEPOSIT_PLUS_BONUS'
                        )
                      }}
                    </option>
                    <option value="bet">
                      {{
                        t(
                          'AILAB.ASSISTANT_V2.PRODUCT_PRICE.OPTIONS.TURNOVER_BASE.BET'
                        )
                      }}
                    </option>
                  </select>
                </div>
                <div class="field">
                  <label>{{
                    t(
                      'AILAB.ASSISTANT_V2.PRODUCT_PRICE.FIELDS.TURNOVER.DEADLINE_DAYS'
                    )
                  }}</label>
                  <input
                    v-model="promoForm.turnover.deadline_days"
                    class="input"
                    :placeholder="
                      t(
                        'AILAB.ASSISTANT_V2.PRODUCT_PRICE.PLACEHOLDERS.DEADLINE_DAYS_EXAMPLE'
                      )
                    "
                  />
                </div>
                <div class="field">
                  <label>{{
                    t(
                      'AILAB.ASSISTANT_V2.PRODUCT_PRICE.FIELDS.TURNOVER.CONTRIBUTION'
                    )
                  }}</label>
                  <input
                    v-model="promoForm.turnover.contribution"
                    class="input"
                    :placeholder="
                      t(
                        'AILAB.ASSISTANT_V2.PRODUCT_PRICE.PLACEHOLDERS.CONTRIBUTION_EXAMPLE'
                      )
                    "
                  />
                </div>
              </div>
              <div class="field">
                <label>{{
                  t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.FIELDS.TURNOVER.RULES')
                }}</label>
                <textarea
                  v-model="promoForm.turnover.eligible_rules"
                  class="textarea"
                  :placeholder="
                    t(
                      'AILAB.ASSISTANT_V2.PRODUCT_PRICE.PLACEHOLDERS.ELIGIBLE_RULES_SHORT'
                    )
                  "
                />
              </div>
              <div class="field">
                <textarea
                  v-model="promoForm.turnover.excluded_rules"
                  class="textarea"
                  :placeholder="
                    t(
                      'AILAB.ASSISTANT_V2.PRODUCT_PRICE.PLACEHOLDERS.EXCLUDED_RULES_SHORT'
                    )
                  "
                />
              </div>
            </div>

            <div class="section">
              <div class="section-title">
                {{
                  t(
                    'AILAB.ASSISTANT_V2.PRODUCT_PRICE.SECTIONS.ELIGIBLE_GAMES'
                  )
                }}
              </div>
              <div class="field">
                <label>{{
                  t(
                    'AILAB.ASSISTANT_V2.PRODUCT_PRICE.FIELDS.ELIGIBLE.PROVIDERS'
                  )
                }}</label>
                <input
                  :value="(promoForm.eligible.providers || []).join(', ')"
                  class="input"
                  :placeholder="
                    t(
                      'AILAB.ASSISTANT_V2.PRODUCT_PRICE.PLACEHOLDERS.PROVIDERS_EXAMPLE'
                    )
                  "
                  @input="
                    promoForm.eligible.providers = ($event.target.value || '')
                      .split(',')
                      .map(s => s.trim())
                      .filter(Boolean)
                  "
                />
              </div>
              <div class="grid2">
                <div class="field">
                  <label>{{
                    t(
                      'AILAB.ASSISTANT_V2.PRODUCT_PRICE.FIELDS.ELIGIBLE.BET_LIMIT'
                    )
                  }}</label>
                  <input
                    v-model="promoForm.eligible.bet_limit"
                    class="input"
                    :placeholder="
                      t(
                        'AILAB.ASSISTANT_V2.PRODUCT_PRICE.PLACEHOLDERS.BET_LIMIT_EXAMPLE'
                      )
                    "
                  />
                </div>
                <div class="field">
                  <label>{{
                    t(
                      'AILAB.ASSISTANT_V2.PRODUCT_PRICE.FIELDS.ELIGIBLE.MAX_WIN'
                    )
                  }}</label>
                  <input
                    v-model="promoForm.eligible.max_win"
                    class="input"
                    :placeholder="
                      t(
                        'AILAB.ASSISTANT_V2.PRODUCT_PRICE.PLACEHOLDERS.MAX_WIN_EXAMPLE'
                      )
                    "
                  />
                </div>
              </div>
              <div class="field">
                <label>{{
                  t(
                    'AILAB.ASSISTANT_V2.PRODUCT_PRICE.FIELDS.ELIGIBLE.EXCLUDED_GAMES'
                  )
                }}</label>
                <textarea
                  v-model="promoForm.eligible.excluded_games"
                  class="textarea"
                  :placeholder="
                    t(
                      'AILAB.ASSISTANT_V2.PRODUCT_PRICE.PLACEHOLDERS.EXCLUDED_GAMES'
                    )
                  "
                />
              </div>
            </div>

            <div class="section">
              <div class="section-title">
                {{
                  t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.SECTIONS.ELIGIBILITY')
                }}
              </div>
              <div class="grid2">
                <div class="field">
                  <label>{{
                    t(
                      'AILAB.ASSISTANT_V2.PRODUCT_PRICE.FIELDS.ELIGIBILITY.CUSTOMER_TYPE'
                    )
                  }}</label>
                  <select
                    v-model="promoForm.eligibility.customer_type"
                    class="input"
                  >
                    <option value="all">
                      {{
                        t(
                          'AILAB.ASSISTANT_V2.PRODUCT_PRICE.OPTIONS.CUSTOMER_TYPE.ALL'
                        )
                      }}
                    </option>
                    <option value="new">
                      {{
                        t(
                          'AILAB.ASSISTANT_V2.PRODUCT_PRICE.OPTIONS.CUSTOMER_TYPE.NEW'
                        )
                      }}
                    </option>
                    <option value="existing">
                      {{
                        t(
                          'AILAB.ASSISTANT_V2.PRODUCT_PRICE.OPTIONS.CUSTOMER_TYPE.EXISTING'
                        )
                      }}
                    </option>
                    <option value="vip">
                      {{
                        t(
                          'AILAB.ASSISTANT_V2.PRODUCT_PRICE.OPTIONS.CUSTOMER_TYPE.VIP'
                        )
                      }}
                    </option>
                  </select>
                </div>
                <div class="field">
                  <label>{{
                    t(
                      'AILAB.ASSISTANT_V2.PRODUCT_PRICE.FIELDS.ELIGIBILITY.KYC_REQUIRED'
                    )
                  }}</label>
                  <select
                    v-model="promoForm.eligibility.kyc_required"
                    class="input"
                  >
                    <option :value="false">
                      {{
                        t(
                          'AILAB.ASSISTANT_V2.PRODUCT_PRICE.OPTIONS.YES_NO.NO'
                        )
                      }}
                    </option>
                    <option :value="true">
                      {{
                        t(
                          'AILAB.ASSISTANT_V2.PRODUCT_PRICE.OPTIONS.YES_NO.YES'
                        )
                      }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="checks">
                <label class="check"
                  ><input
                    v-model="promoForm.eligibility.one_per_person"
                    type="checkbox"
                  />
                  {{
                    t(
                      'AILAB.ASSISTANT_V2.PRODUCT_PRICE.FIELDS.ELIGIBILITY.ONE_PER_PERSON'
                    )
                  }}</label
                >
                <label class="check"
                  ><input
                    v-model="promoForm.eligibility.one_per_ip"
                    type="checkbox"
                  />
                  {{
                    t(
                      'AILAB.ASSISTANT_V2.PRODUCT_PRICE.FIELDS.ELIGIBILITY.ONE_PER_IP'
                    )
                  }}</label
                >
                <label class="check"
                  ><input
                    v-model="promoForm.eligibility.one_per_device"
                    type="checkbox"
                  />
                  {{
                    t(
                      'AILAB.ASSISTANT_V2.PRODUCT_PRICE.FIELDS.ELIGIBILITY.ONE_PER_DEVICE'
                    )
                  }}</label
                >
              </div>
            </div>

            <div class="section">
              <div class="section-title">
                {{ t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.SECTIONS.CLAIMING') }}
              </div>
              <div class="grid2">
                <div class="field">
                  <label>{{
                    t(
                      'AILAB.ASSISTANT_V2.PRODUCT_PRICE.FIELDS.CLAIMING.METHOD'
                    )
                  }}</label>
                  <select v-model="promoForm.claiming.method" class="input">
                    <option value="auto">
                      {{
                        t(
                          'AILAB.ASSISTANT_V2.PRODUCT_PRICE.OPTIONS.CLAIM_METHOD.AUTO'
                        )
                      }}
                    </option>
                    <option value="code">
                      {{
                        t(
                          'AILAB.ASSISTANT_V2.PRODUCT_PRICE.OPTIONS.CLAIM_METHOD.CODE'
                        )
                      }}
                    </option>
                    <option value="admin">
                      {{
                        t(
                          'AILAB.ASSISTANT_V2.PRODUCT_PRICE.OPTIONS.CLAIM_METHOD.ADMIN'
                        )
                      }}
                    </option>
                  </select>
                </div>
                <div class="field">
                  <label>{{
                    t(
                      'AILAB.ASSISTANT_V2.PRODUCT_PRICE.FIELDS.CLAIMING.PRECONDITIONS'
                    )
                  }}</label>
                  <input
                    v-model="promoForm.claiming.preconditions"
                    class="input"
                    :placeholder="
                      t(
                        'AILAB.ASSISTANT_V2.PRODUCT_PRICE.PLACEHOLDERS.PRECONDITIONS_EXAMPLE'
                      )
                    "
                  />
                </div>
              </div>
            </div>

            <div class="section">
              <div class="section-title">
                {{
                  t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.SECTIONS.CANNOT_CLAIM')
                }}
              </div>
              <textarea
                v-model="promoForm.cannot_claim.reasons"
                class="textarea"
                :placeholder="
                  t(
                    'AILAB.ASSISTANT_V2.PRODUCT_PRICE.PLACEHOLDERS.CANNOT_CLAIM_REASONS'
                  )
                "
              />
            </div>

            <div class="section">
              <div class="section-title">
                {{
                  t(
                    'AILAB.ASSISTANT_V2.PRODUCT_PRICE.SECTIONS.DISQUALIFICATIONS'
                  )
                }}
              </div>
              <textarea
                v-model="promoForm.disqualifications.rules"
                class="textarea"
                :placeholder="
                  t(
                    'AILAB.ASSISTANT_V2.PRODUCT_PRICE.PLACEHOLDERS.DISQUALIFICATIONS'
                  )
                "
              />
            </div>

            <div class="section">
              <div class="section-title">
                {{
                  t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.SECTIONS.COMPLIANCE')
                }}
              </div>
              <div class="checks">
                <label class="check"
                  ><input
                    v-model="promoForm.compliance.age_18_plus"
                    type="checkbox"
                  />
                  {{
                    t(
                      'AILAB.ASSISTANT_V2.PRODUCT_PRICE.FIELDS.COMPLIANCE.AGE_18_PLUS'
                    )
                  }}</label
                >
                <label class="check"
                  ><input
                    v-model="promoForm.compliance.no_guaranteed_wins"
                    type="checkbox"
                  />
                  {{
                    t(
                      'AILAB.ASSISTANT_V2.PRODUCT_PRICE.FIELDS.COMPLIANCE.NO_GUARANTEED_WINS'
                    )
                  }}</label
                >
                <label class="check"
                  ><input
                    v-model="promoForm.compliance.terms_may_change"
                    type="checkbox"
                  />
                  {{
                    t(
                      'AILAB.ASSISTANT_V2.PRODUCT_PRICE.FIELDS.COMPLIANCE.TERMS_MAY_CHANGE'
                    )
                  }}</label
                >
              </div>
              <textarea
                v-model="promoForm.compliance.notes"
                class="textarea"
                :placeholder="
                  t(
                    'AILAB.ASSISTANT_V2.PRODUCT_PRICE.PLACEHOLDERS.ADDITIONAL_NOTES'
                  )
                "
              />
            </div>
          </div>

          <div class="modal-footer">
            <button class="btn" type="button" @click="closePromoDetails">
              {{ t('AILAB.FORM.CANCEL') }}
            </button>
            <button
              class="btn primary"
              type="button"
              :disabled="isSubmitting"
              @click="persistPromoDetails"
            >
              <span
                v-if="isSubmitting"
                class="i-lucide-loader-2 size-4 animate-spin"
              />
              {{ t('AILAB.ASSISTANT_V2.PRODUCT_PRICE.MODAL.SAVE_DETAILS') }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.price-tab {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 80px;
  --price-action-bg: #ffffff;
  --price-action-text: #000000;
  --price-action-hover-bg: #e5e7eb;
  --price-action-shadow: 0 2px 8px rgba(255, 255, 255, 0.1);
  --price-muted-hover-bg: rgba(255, 255, 255, 0.1);
  --price-focus-border: rgba(255, 255, 255, 0.2);
  --price-accent-soft: rgba(255, 255, 255, 0.12);
  --price-accent-soft-hover: rgba(255, 255, 255, 0.18);
  --price-accent-text: #ffffff;
  --price-currency-bg: rgba(255, 255, 255, 0.1);
  --price-currency-border: rgba(0, 102, 255, 0.2);
  --price-currency-text: #ffffff;
  --price-add-row-hover-border: rgba(255, 255, 255, 0.2);
  --price-add-row-hover-color: #ffffff;
  --price-close-hover: rgba(255, 255, 255, 0.06);
}

:global(body:not(.dark) .price-tab) {
  --price-action-bg: #0066ff;
  --price-action-text: #ffffff;
  --price-action-hover-bg: linear-gradient(135deg, #3388ff 0%, #0066ff 100%);
  --price-action-shadow: 0 2px 10px rgba(0, 102, 255, 0.32);
  --price-muted-hover-bg: rgba(15, 23, 42, 0.06);
  --price-focus-border: rgba(0, 102, 255, 0.5);
  --price-accent-soft: rgba(0, 102, 255, 0.12);
  --price-accent-soft-hover: rgba(0, 102, 255, 0.2);
  --price-accent-text: #004db3;
  --price-currency-bg: rgba(0, 102, 255, 0.12);
  --price-currency-border: rgba(0, 102, 255, 0.3);
  --price-currency-text: #004db3;
  --price-add-row-hover-border: rgba(0, 102, 255, 0.45);
  --price-add-row-hover-color: #0058d8;
  --price-close-hover: rgba(15, 23, 42, 0.06);
}

/* Toolbar */
.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: var(--wlab-bg-tertiary);
  border: 1px solid var(--wlab-border-light);
  border-radius: 10px;
}

.default-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 500;
  background: var(--wlab-bg-input);
  border: 1px solid var(--wlab-border-default);
  border-radius: 8px;
  color: var(--wlab-text-secondary);
  cursor: pointer;
  transition: all 0.2s;
}

.default-btn:hover {
  background: var(--price-muted-hover-bg);
}

.toolbar-actions {
  display: flex;
  gap: 10px;
}

.edit-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  font-size: 13px;
  font-weight: 600;
  background: var(--price-action-bg);
  border: none;
  border-radius: 8px;
  color: var(--price-action-text);
  cursor: pointer;
  transition: all 0.2s;
}

.edit-btn:hover {
  background: var(--price-action-hover-bg);
  box-shadow: var(--price-action-shadow);
}

.cancel-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 500;
  background: transparent;
  border: 1px solid var(--wlab-border-default);
  border-radius: 8px;
  color: var(--wlab-text-secondary);
  cursor: pointer;
}

.save-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  font-size: 13px;
  font-weight: 600;
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  border: none;
  border-radius: 8px;
  color: #ffffff;
  cursor: pointer;
  transition: all 0.2s;
}

.save-btn:hover:not(:disabled) {
  box-shadow: 0 4px 12px rgba(34, 197, 94, 0.3);
}

.save-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* AI gamer promo guide */
.promo-guide {
  background: var(--wlab-bg-tertiary);
  border: 1px solid var(--wlab-border-light);
  border-radius: 12px;
  padding: 14px 16px;
}

.promo-guide-title {
  font-size: 13px;
  font-weight: 700;
  color: var(--wlab-text-primary);
  margin-bottom: 8px;
}

.promo-guide-list {
  margin: 0;
  padding-left: 18px;
  color: var(--wlab-text-secondary);
  font-size: 13px;
  line-height: 1.4;
}

.promo-guide-example {
  margin-top: 10px;
  font-size: 12px;
  color: var(--wlab-text-muted);
}

.promo-guide-example code {
  color: var(--wlab-text-primary);
  background: var(--wlab-bg-input);
  border: 1px solid var(--wlab-border-default);
  padding: 2px 6px;
  border-radius: 6px;
}

.promo-name-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.promo-name {
  font-weight: 700;
  color: var(--wlab-text-primary);
}

.promo-sub {
  font-size: 12px;
  color: var(--wlab-text-muted);
}

/* Price Container */
.price-container {
  background: var(--wlab-bg-tertiary);
  border: 1px solid var(--wlab-border-light);
  border-radius: 12px;
  padding: 16px;
  overflow-x: auto;
}

/* Price Table */
.price-table {
  width: 100%;
  border-collapse: collapse;
}

.price-table th {
  padding: 12px 16px;
  font-size: 12px;
  font-weight: 600;
  color: var(--wlab-text-muted);
  text-align: left;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid var(--wlab-border-light);
}

.price-table td {
  padding: 14px 16px;
  font-size: 14px;
  color: var(--wlab-text-primary);
  border-bottom: 1px solid var(--wlab-border-light);
}

.price-table tr:hover {
  background: var(--wlab-bg-tertiary);
}

.country-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--wlab-text-primary);
  font-weight: 500;
}

.currency-badge {
  display: inline-block;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  background: var(--price-currency-bg);
  border: 1px solid var(--price-currency-border);
  border-radius: 6px;
  color: var(--price-currency-text);
}

.discount-price {
  color: #22c55e;
  font-weight: 600;
}

.table-input {
  width: 100%;
  padding: 8px 12px;
  font-size: 14px;
  color: var(--wlab-text-primary);
  background: var(--wlab-bg-input);
  border: 1px solid var(--wlab-border-default);
  border-radius: 6px;
}

.table-input:focus {
  outline: none;
  border-color: var(--price-focus-border);
}

.table-input.small {
  max-width: 80px;
}

.delete-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(239, 68, 68, 0.1);
  border: none;
  border-radius: 6px;
  color: #ef4444;
  cursor: pointer;
  transition: all 0.2s;
}

.delete-btn:hover {
  background: rgba(239, 68, 68, 0.2);
}

.row-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.details-btn {
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 600;
  background: var(--price-accent-soft);
  border: 1px solid var(--price-currency-border);
  border-radius: 8px;
  color: var(--price-accent-text);
  cursor: pointer;
}

.details-btn:hover {
  background: var(--price-accent-soft-hover);
}

/* Modal (Promotion Details) */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 300;
}

.modal-content {
  width: 100%;
  max-width: 860px;
  max-height: 85vh;
  background: var(--wlab-bg-primary);
  border: 1px solid var(--wlab-border-default);
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 1px solid var(--wlab-border-light);
}

.modal-close {
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: var(--wlab-text-muted);
  cursor: pointer;
}

.modal-close:hover {
  background: var(--price-close-hover);
  color: var(--wlab-text-primary);
}

.modal-body {
  padding: 16px 18px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.modal-note {
  font-size: 13px;
  color: var(--wlab-text-secondary);
  background: var(--wlab-bg-tertiary);
  border: 1px solid var(--wlab-border-light);
  border-radius: 10px;
  padding: 12px 14px;
}

.section {
  background: var(--wlab-bg-tertiary);
  border: 1px solid var(--wlab-border-light);
  border-radius: 12px;
  padding: 14px;
}

.section-title {
  font-size: 13px;
  font-weight: 700;
  color: var(--wlab-text-primary);
  margin-bottom: 10px;
}

.grid2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field label {
  font-size: 12px;
  font-weight: 600;
  color: var(--wlab-text-secondary);
}

.input,
.textarea {
  width: 100%;
  padding: 10px 12px;
  font-size: 14px;
  color: var(--wlab-text-primary);
  background: var(--wlab-bg-input);
  border: 1px solid var(--wlab-border-default);
  border-radius: 8px;
}

.textarea {
  min-height: 90px;
  resize: vertical;
}

.checks {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 6px;
}

.check {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--wlab-text-secondary);
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 18px;
  border-top: 1px solid var(--wlab-border-light);
}

.btn {
  padding: 10px 18px;
  font-size: 13px;
  font-weight: 600;
  background: transparent;
  border: 1px solid var(--wlab-border-default);
  border-radius: 10px;
  color: var(--wlab-text-secondary);
  cursor: pointer;
}

.btn.primary {
  background: var(--price-action-bg);
  border: none;
  color: var(--price-action-text);
}

.btn.primary:hover:not(:disabled) {
  background: var(--price-action-hover-bg);
  box-shadow: var(--price-action-shadow);
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.add-row-container {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--wlab-border-light);
}

.add-row-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  font-size: 13px;
  font-weight: 500;
  background: transparent;
  border: 1px dashed var(--wlab-text-muted);
  border-radius: 8px;
  color: var(--wlab-text-tertiary);
  cursor: pointer;
  transition: all 0.2s;
}

.add-row-btn:hover {
  border-color: var(--price-add-row-hover-border);
  color: var(--price-add-row-hover-color);
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
}

.empty-state p {
  margin: 16px 0;
  font-size: 14px;
  color: var(--wlab-text-muted);
}

.add-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 600;
  background: var(--price-action-bg);
  border: none;
  border-radius: 10px;
  color: var(--price-action-text);
  cursor: pointer;
  transition: all 0.2s ease;
}

.add-btn:hover {
  background: var(--price-action-hover-bg);
  box-shadow: var(--price-action-shadow);
}
</style>
